// plaidItemHealth.js — when a stored bank balance is really from, and whether
// a linked bank needs the user (2026-10-29). Pure; shared by the client
// (Scorify.jsx), api/lib/plaid-sync.js (writes), api/lib/goal-progress.js and
// api/lib/coach-run.js (Kai's freshness window). Copy carries no dashes.
//
// Every sync reads Plaid's free /item/get and stores per Item:
//   plaid_synced_at    status.transactions.last_successful_update (Plaid's clock)
//   item_error         item.error.error_code, or null
//   item_checked_at    when that read happened (null on rows never checked)
// The balance a user sees is only as fresh as the OLDEST Item behind it.
import { balanceAsOfLabel } from './balanceAsOf.js';

export const PLAID_ITEM_STALE_MS = 24 * 60 * 60 * 1000;
export const BANK_ATTENTION_TITLE = 'Your bank connection needs attention';
export const BANK_ATTENTION_HINT = 'Balances from this bank are treated as out of date until it is reconnected.';

const DEMO = (it) => typeof it?.item_id === 'string' && it.item_id.startsWith('demo_');
const ms = (v) => { const n = v == null ? NaN : (typeof v === 'number' ? v : Date.parse(String(v))); return Number.isFinite(n) ? n : null; };
// Server rows (plaid_item_error, plaid_item_checked_at) and client items
// (item_error, item_checked_at) read through one shape.
const norm = (it) => ({
  syncedAt: it?.plaid_synced_at ?? null,
  error: (it?.item_error ?? it?.plaid_item_error) || null,
  checkedAt: it?.item_checked_at ?? it?.plaid_item_checked_at ?? null,
});

/**
 * One Item's health: { needsAttention, reason: 'error' | 'stale' | null, errorCode }.
 * An item.error of any code (ITEM_LOGIN_REQUIRED, PENDING_EXPIRATION, ...) or a
 * Plaid refresh older than 24h needs the user. An Item never checked, or one
 * Plaid has not synced yet (no last_successful_update, no error), is not flagged.
 */
export function itemHealth(item, now = Date.now()) {
  const { syncedAt, error, checkedAt } = norm(item);
  if (error) return { needsAttention: true, reason: 'error', errorCode: String(error) };
  if (!checkedAt) return { needsAttention: false, reason: null, errorCode: null };
  const at = ms(syncedAt);
  if (at != null && now - at > PLAID_ITEM_STALE_MS) return { needsAttention: true, reason: 'stale', errorCode: null };
  return { needsAttention: false, reason: null, errorCode: null };
}

/**
 * When the user's balances were last refreshed FROM THE BANK, across Items:
 *   { known: false, at: null }        no Items, or one was never checked (legacy row)
 *   { known: true,  at: null }        an Item is broken or has no Plaid sync time: not vouchable
 *   { known: true,  at: <iso> }       the OLDEST plaid_synced_at
 */
export function bankSyncedAt(items) {
  const list = (items || []).filter((it) => it && !DEMO(it));
  if (!list.length) return { known: false, at: null };
  const n = list.map(norm);
  if (n.some((x) => !x.checkedAt)) return { known: false, at: null };
  if (n.some((x) => x.error || ms(x.syncedAt) == null)) return { known: true, at: null };
  const oldest = n.reduce((acc, x) => (acc == null || ms(x.syncedAt) < ms(acc) ? x.syncedAt : acc), null);
  return { known: true, at: new Date(ms(oldest)).toISOString() };
}

/**
 * The timestamp Kai's 12h freshness window should judge a plaid_cache row by:
 * bank_synced_at once the row has been status-checked (null there means a
 * broken Item: stale), else the legacy balances_updated_at write time.
 */
export function effectiveBalanceFreshAt(cacheRow) {
  if (!cacheRow) return null;
  if (cacheRow.bank_status_checked_at) return cacheRow.bank_synced_at || null;
  return cacheRow.balances_updated_at || null;
}

/** The sentence under the attention title for one Item. */
export function bankAttentionLine(item, name = 'Your bank', now = Date.now()) {
  const h = itemHealth(item, now);
  if (h.reason === 'error') return `${name} reported a problem with the connection.`;
  const label = balanceAsOfLabel(item?.plaid_synced_at, { now });
  const since = label ? label.replace(/^As of /, '') : null;
  return since ? `${name} has not sent new data since ${since}.` : `${name} has not sent new data recently.`;
}
